/**
 * Ignored parts tab: table with NEX matched-line collapse and Re-include part.
 */
import React, { useMemo } from 'react';
import {
    Box,
    Button,
    HStack,
    Icon,
    Table,
    Tbody,
    Td,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { TbPlus } from 'react-icons/tb';
import type { InvoiceLineItemsSectionProps } from './InvoiceLineItemsSection';
import { NexMatchSourceBadge } from './NexMatchSourceBadge';
import { getSignedNexLineDisplayQty, getSignedNexLineDisplayTotal } from '../../utils/nexLineQuantity';
import { ignoredRowGlobalPartIndex, isIgnoredRowMeaningful } from '../../utils/returnSlipSubmitPreview';
import { formatNexMatchedLinePoLabel } from '../../utils/nexMatchedLinePoLabel';
import {
    INVOICE_TAB_ACCENT,
    INVOICE_TAB_ACCENT_HOVER,
    InvoiceIssueFlagButton,
    InvoicePartGridTableContainer,
    InvoiceMatchedNexExpandAllHeaderControl,
    InvoiceMatchedNexRows,
    InvoiceMatchedNexToggleButton,
    InvoiceSuggestionBulbButton,
    InvoiceStandardHeaderRow,
    buildListPriceHeaderContent,
    getInvoiceItemsTableSx,
    InvoicePartTableRowActionsCell,
    VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON,
    invoicePartGridLandingInputPropsWithCaretAtEnd,
} from './InvoiceTabShared';
import { parseSignedQtyCommit } from '../../utils/invoiceNumericInput';
import { LineItemGridLandingEditable } from './LineItemGridLandingEditable';
import { useInvoicePartGridInteractions } from './useInvoicePartGridInteractions';
import { useInvoicePartTableRowDrag } from './useInvoicePartTableRowDrag';
import type { InvoicePart } from '../../types/invoice';

export function InvoiceIgnoredPartsTab(props: InvoiceLineItemsSectionProps) {
    const {
        invoice,
        editedFields,
        expandedMatchedNexIgnoredRows,
        setExpandedMatchedNexIgnoredRows,
        addIgnoredPart,
        addIgnoredPartAt,
        addIgnoredPartAbove,
        removeIgnoredPart,
        reorderIgnoredParts,
        restoreIgnoredPart,
        updateIgnoredPart,
        getIgnoredPartStringValue,
        renderIgnoredPartNumericCell,
        fmtUsd,
        isReadOnly,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        flaggedPartIndices,
        getIssuesForPart,
        openReconciliationDetailModal,
        getIssueTooltip,
        getPartNumberChoiceIssue,
        getMissingInNexSuggestion,
        setUnmatchConfirmModal,
        effectiveMatchedNexLines,
        setEditingNumericField,
        setEditingNumericValues,
        onLineItemImageHighlightChange,
        showTableGridlines = true,
        nexLineQuantityBasis = 'qtyRec',
    } = props as InvoiceLineItemsSectionProps & { isReadOnly?: boolean };

    const ignoredList = useMemo(
        () =>
            editedFields.ignoredParts ??
            invoice?.ignoredParts ??
            (invoice as { canceledParts?: typeof invoice.ignoredParts })?.canceledParts ??
            [],
        [editedFields.ignoredParts, invoice]
    );

    const matchedNexForIgnored = useMemo(
        () =>
            (
                editedFields.reconciliationIssues as
                    | { matchedNexLinesForIgnored?: Array<{ ignoredIndex: number; nexLine: unknown }> }
                    | undefined
            )?.matchedNexLinesForIgnored ?? invoice?.reconciliationIssues?.matchedNexLinesForIgnored ?? [],
        [editedFields.reconciliationIssues, invoice]
    );

    const matchedNexByIgnoredIndex = useMemo(
        () => Object.fromEntries(matchedNexForIgnored.map((m: { ignoredIndex: number; nexLine: any }) => [m.ignoredIndex, m.nexLine])),
        [matchedNexForIgnored]
    );

    const matchedNexLinesAll = useMemo(
        () => effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [],
        [effectiveMatchedNexLines, invoice]
    );

    const isReturnSlip = invoice?.invoiceType === 'return_slip';
    const lineItemsForGlobal = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightForGlobal = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const fuelForGlobal = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const restockingForGlobal = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const discountForGlobal = editedFields.discountItems ?? invoice?.discountItems ?? [];
    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const vendorMismatchDisablesSuggestions = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const hasItems = ignoredList.length > 0;

    const ignoredIndicesWithMatchedNex = useMemo(() => {
        const indices: number[] = [];
        ignoredList.forEach((ignoredRow: any, index: number) => {
            const g =
                isReturnSlip && isIgnoredRowMeaningful(ignoredRow)
                    ? ignoredRowGlobalPartIndex({
                          ignoredIndex: index,
                          lineItemsLength: lineItemsForGlobal.length,
                          freightItemsLength: freightForGlobal.length,
                          fuelItemsLength: fuelForGlobal.length,
                          restockingItemsLength: restockingForGlobal.length,
                          discountItemsLength: discountForGlobal.length,
                      })
                    : -1;
            const fromIgnoredMap = matchedNexByIgnoredIndex[index] as Record<string, unknown> | undefined;
            let matchedNex = isReturnSlip
                ? ((localMatchedNexOverrides[g] as typeof fromIgnoredMap | undefined) ??
                  matchedNexLinesAll.find((m: { partIndex: number }) => m.partIndex === g)?.nexLine ??
                  fromIgnoredMap)
                : fromIgnoredMap;
            if (isReturnSlip && g >= 0 && unmatchedPartIndices.has(g)) matchedNex = undefined;
            if (matchedNex) indices.push(index);
        });
        return indices;
    }, [
        ignoredList,
        matchedNexByIgnoredIndex,
        isReturnSlip,
        lineItemsForGlobal.length,
        freightForGlobal.length,
        fuelForGlobal.length,
        restockingForGlobal.length,
        discountForGlobal.length,
        localMatchedNexOverrides,
        unmatchedPartIndices,
        matchedNexLinesAll,
    ]);

    const allIgnoredMatchedNexExpanded =
        ignoredIndicesWithMatchedNex.length > 0 && ignoredIndicesWithMatchedNex.every((i) => expandedMatchedNexIgnoredRows.has(i));

    const ignoredRows = ignoredList as ReadonlyArray<InvoicePart>;

    const spreadsheet = useInvoicePartGridInteractions({
        rowCount: ignoredRows.length,
        rows: ignoredRows,
        getStringValue: getIgnoredPartStringValue,
        updateCell: updateIgnoredPart,
        isReadOnly: !!isReadOnly,
        setEditingNumericField: setEditingNumericField ?? (() => {}),
        setEditingNumericValues,
        numericKeyPrefix: 'ignoredPart',
    });

    const rowDrag = useInvoicePartTableRowDrag({ isReadOnly: !!isReadOnly, onReorder: reorderIgnoredParts });

    const gridEditableInputProps = invoicePartGridLandingInputPropsWithCaretAtEnd();
    const listPriceHeaderContent =
        buildListPriceHeaderContent({
            invoiceType: invoice?.invoiceType,
            subletListPriceMarkup: invoice?.subletListPriceMarkup,
            subletListPriceDiscount: invoice?.subletListPriceDiscount,
            lkqListPriceMarkup: invoice?.lkqListPriceMarkup,
            lkqListPriceDiscount: invoice?.lkqListPriceDiscount,
        }) ?? 'List Price';

    if (!hasItems) {
        return (
            <>
                <Box textAlign="center" py={8}>
                    <Button
                        size="sm"
                        bg={INVOICE_TAB_ACCENT}
                        color="white"
                        leftIcon={<Icon as={TbPlus as React.ElementType} boxSize={4} />}
                        onClick={addIgnoredPart}
                        _hover={{ bg: INVOICE_TAB_ACCENT_HOVER }}
                        isDisabled={!!isReadOnly}
                    >
                        Add Ignored Part
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <>
            <InvoicePartGridTableContainer
                containerRef={spreadsheet.containerRef}
                isReadOnly={!!isReadOnly}
                onMouseDownCapture={spreadsheet.onContainerMouseDownCapture}
                onPaste={spreadsheet.onPaste}
            >
                <Table variant="simple" size="sm" sx={getInvoiceItemsTableSx(showTableGridlines)}>
                    <Thead>
                        <InvoiceStandardHeaderRow
                            firstColumnContent={
                                <InvoiceMatchedNexExpandAllHeaderControl
                                    isVisible={ignoredIndicesWithMatchedNex.length > 0}
                                    allExpanded={allIgnoredMatchedNexExpanded}
                                    onToggle={() => {
                                        setExpandedMatchedNexIgnoredRows((prev) => {
                                            const next = new Set(prev);
                                            if (allIgnoredMatchedNexExpanded) ignoredIndicesWithMatchedNex.forEach((i) => next.delete(i));
                                            else ignoredIndicesWithMatchedNex.forEach((i) => next.add(i));
                                            return next;
                                        });
                                    }}
                                />
                            }
                            listPriceHeaderContent={listPriceHeaderContent}
                        />
                    </Thead>
                    <Tbody>
                        {ignoredList.map((ignoredRow: any, index: number) => {
                            const g =
                                isReturnSlip && isIgnoredRowMeaningful(ignoredRow)
                                    ? ignoredRowGlobalPartIndex({
                                          ignoredIndex: index,
                                          lineItemsLength: lineItemsForGlobal.length,
                                          freightItemsLength: freightForGlobal.length,
                                          fuelItemsLength: fuelForGlobal.length,
                                          restockingItemsLength: restockingForGlobal.length,
                                          discountItemsLength: discountForGlobal.length,
                                      })
                                    : -1;
                            const fromIgnoredMap = matchedNexByIgnoredIndex[index] as
                                | {
                                      partDesc?: string;
                                      partNumber?: string;
                                      qtyOnOrder?: number;
                                      qty?: number;
                                      listPrice?: number;
                                      unitCost?: number;
                                      extendedCost?: number;
                                      poHeaderID?: number;
                                      lineNumber?: number;
                                      unqSeq?: number | null;
                                      _poVendorName?: string;
                                  }
                                | undefined;
                            let matchedNex = isReturnSlip
                                ? ((localMatchedNexOverrides[g] as typeof fromIgnoredMap | undefined) ??
                                  matchedNexLinesAll.find((m: { partIndex: number }) => m.partIndex === g)?.nexLine ??
                                  fromIgnoredMap)
                                : fromIgnoredMap;
                            if (isReturnSlip && g >= 0 && unmatchedPartIndices.has(g)) matchedNex = undefined;
                            const hasMatchedNex = !!matchedNex;
                            const isUserUnmatched = isReturnSlip && g >= 0 && unmatchedPartIndices.has(g);
                            const issues = isReturnSlip && g >= 0 && isIgnoredRowMeaningful(ignoredRow) ? getIssuesForPart(g) : [];
                            const isFlagged =
                                !suppressFlagAndSuggestionUi &&
                                isReturnSlip &&
                                g >= 0 &&
                                isIgnoredRowMeaningful(ignoredRow) &&
                                (flaggedPartIndices.has(g) || isUserUnmatched);
                            const showRowHighlight =
                                isReturnSlip &&
                                isIgnoredRowMeaningful(ignoredRow) &&
                                !hasMatchedNex &&
                                isFlagged;
                            const isNexExpanded = expandedMatchedNexIgnoredRows.has(index);
                            const nexQty = getSignedNexLineDisplayQty(
                                matchedNex,
                                invoice?.invoiceType,
                                nexLineQuantityBasis,
                            );
                            const nexTotalRaw = matchedNex?.extendedCost ?? (Math.abs(nexQty) * (matchedNex?.unitCost ?? 0));
                            const nexTotal = getSignedNexLineDisplayTotal(nexTotalRaw, invoice?.invoiceType);
                            const nexMatchedVendorName = matchedNex?._poVendorName?.trim() ?? '';
                            const { label: nexMatchedPoLineLabel, status: nexMatchedStatus } = formatNexMatchedLinePoLabel(
                                matchedNex,
                                invoice?.nexResponse ?? null,
                            );
                            return (
                                <React.Fragment key={index}>
                                    <Tr
                                        bg={showRowHighlight ? 'orange.50' : 'white'}
                                        borderLeft={showRowHighlight ? '3px solid' : undefined}
                                        borderLeftColor={showRowHighlight ? 'orange.400' : undefined}
                                        _hover={{ bg: showRowHighlight ? 'orange.100' : 'gray.50' }}
                                        role="group"
                                        {...rowDrag.getDraggableRowProps(index)}
                                        onMouseEnter={() => {
                                            if (ignoredRow?.imageSourceHighlight) {
                                                onLineItemImageHighlightChange?.(ignoredRow.imageSourceHighlight);
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            onLineItemImageHighlightChange?.(null);
                                        }}
                                    >
                                        <Td py={3} px={2} borderColor="gray.100" position="relative" w="116px" minW="116px">
                                            <InvoicePartTableRowActionsCell
                                                isReadOnly={!!isReadOnly}
                                                onAddAbove={() => addIgnoredPartAbove(index)}
                                                onAddBelow={() => addIgnoredPartAt(index)}
                                                onDelete={() => removeIgnoredPart(index)}
                                                onRestore={() => restoreIgnoredPart(index)}
                                            />
                                            <HStack spacing={0.5} align="center" justify="flex-start" pl={5} flexWrap="nowrap">
                                                    {isReturnSlip && isIgnoredRowMeaningful(ignoredRow) && g >= 0 && (
                                                        <>
                                                            {hasMatchedNex && (
                                                                <InvoiceMatchedNexToggleButton
                                                                    isExpanded={isNexExpanded}
                                                                    onClick={() =>
                                                                        setExpandedMatchedNexIgnoredRows((prev) => {
                                                                            const next = new Set(prev);
                                                                            if (next.has(index)) next.delete(index);
                                                                            else next.add(index);
                                                                            return next;
                                                                        })
                                                                    }
                                                                />
                                                            )}
                                                            {!suppressFlagAndSuggestionUi && !hasMatchedNex && isUserUnmatched && (
                                                                <InvoiceIssueFlagButton tooltip="Line will be submitted without a matched NEX return line" ariaLabel="Unmatched return slip line" />
                                                            )}
                                                            {!suppressFlagAndSuggestionUi && !hasMatchedNex && !isUserUnmatched && isFlagged && issues.length > 0 && (
                                                                <InvoiceIssueFlagButton tooltip={getIssueTooltip(issues)} />
                                                            )}
                                                            {!suppressFlagAndSuggestionUi && !hasMatchedNex && (
                                                                <InvoiceSuggestionBulbButton
                                                                    tooltip={
                                                                        getPartNumberChoiceIssue(g)
                                                                            ? 'Part number differs from NEX'
                                                                            : getMissingInNexSuggestion(g)
                                                                              ? 'Part not found – possible match'
                                                                              : 'Click to view NEX /returns lines for this RO'
                                                                    }
                                                                    ariaLabel="Open NEX match options"
                                                                    disabled={!!isReadOnly}
                                                                    onClick={() => {
                                                                        const iss = getIssuesForPart(g);
                                                                        openReconciliationDetailModal({ type: 'flag', partIndex: g, issue: iss[0] });
                                                                    }}
                                                                />
                                                            )}
                                                            {vendorMismatchDisablesSuggestions && !hasMatchedNex && !isReadOnly && (
                                                                <InvoiceSuggestionBulbButton
                                                                    tooltip={VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON}
                                                                    disabled
                                                                    ariaLabel="Open NEX match options disabled due to vendor mismatch"
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                    {!isReturnSlip && hasMatchedNex && (
                                                        <InvoiceMatchedNexToggleButton
                                                            isExpanded={isNexExpanded}
                                                            onClick={() =>
                                                                setExpandedMatchedNexIgnoredRows((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(index)) next.delete(index);
                                                                    else next.add(index);
                                                                    return next;
                                                                })
                                                            }
                                                        />
                                                    )}
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            color="gray.900"
                                            textAlign="left"
                                            borderColor="gray.100"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={0}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 0)}
                                        >
                                            <LineItemGridLandingEditable
                                                value={getIgnoredPartStringValue(index, 'title', '')}
                                                onChange={(v) => updateIgnoredPart(index, 'title', v)}
                                                placeholder="Description"
                                                isReadOnly={!!isReadOnly}
                                                editableRootProps={{ w: '100%' }}
                                                editablePreviewProps={{
                                                    display: 'block',
                                                    w: '100%',
                                                    textAlign: 'left',
                                                    color: 'gray.900',
                                                    py: 0,
                                                    px: 0,
                                                }}
                                                editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                            />
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="120px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={1}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 1)}
                                        >
                                            <LineItemGridLandingEditable
                                                value={getIgnoredPartStringValue(index, 'partNumber', '')}
                                                onChange={(v) => updateIgnoredPart(index, 'partNumber', v)}
                                                placeholder="Part #"
                                                isReadOnly={!!isReadOnly}
                                                editableRootProps={{ flex: '1', minW: 0 }}
                                                editablePreviewProps={{
                                                    w: '100%',
                                                    cursor: 'pointer',
                                                    color: 'gray.900',
                                                    py: 0,
                                                    px: 0,
                                                    whiteSpace: 'nowrap',
                                                }}
                                                editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                            />
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={2}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 2)}
                                        >
                                            <LineItemGridLandingEditable
                                                value={getIgnoredPartStringValue(index, 'qty', '0')}
                                                onChange={(v) => updateIgnoredPart(index, 'qty', parseSignedQtyCommit(v))}
                                                isReadOnly={!!isReadOnly}
                                                editableRootProps={{ flex: '1', textAlign: 'right' }}
                                                editablePreviewProps={{
                                                    w: '100%',
                                                    cursor: 'pointer',
                                                    textAlign: 'right',
                                                    color: 'gray.900',
                                                    py: 0,
                                                    px: 0,
                                                }}
                                                editableInputProps={{
                                                    type: 'text',
                                                    inputMode: 'text',
                                                    ...gridEditableInputProps,
                                                    textAlign: 'right',
                                                }}
                                            />
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={3}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 3)}
                                        >
                                            {renderIgnoredPartNumericCell(index, 'listPrice')}
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={4}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 4)}
                                        >
                                            {renderIgnoredPartNumericCell(index, 'unitBuyPrice')}
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            fontWeight="medium"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={5}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 5)}
                                        >
                                            {renderIgnoredPartNumericCell(index, 'totalBuyPrice', 'medium')}
                                        </Td>
                                    </Tr>
                                    {hasMatchedNex ? (
                                        <InvoiceMatchedNexRows
                                            isExpanded={isNexExpanded}
                                                matchedNex={matchedNex!}
                                            nexQty={nexQty}
                                            nexTotal={nexTotal}
                                            nexMatchedVendorName={nexMatchedVendorName}
                                                nexMatchedPoLineLabel={nexMatchedPoLineLabel ?? undefined}
                                                nexMatchedStatus={nexMatchedStatus ?? undefined}
                                            showTableGridlines={showTableGridlines}
                                            isReadOnly={!!isReadOnly || !(isReturnSlip && g >= 0)}
                                            onUnmatch={
                                                isReturnSlip && g >= 0
                                                    ? () =>
                                                          setUnmatchConfirmModal({
                                                              partIndex: g,
                                                              partNumber: ignoredRow?.partNumber,
                                                              nexPartNumber: matchedNex?.partNumber,
                                                              nexLine: matchedNex,
                                                          })
                                                    : undefined
                                            }
                                            formatMoney={fmtUsd}
                                            sourceBadge={
                                                <NexMatchSourceBadge
                                                    source={(matchedNex as { _nexMatchSource?: 'returns' | 'orders' } | undefined)?._nexMatchSource}
                                                />
                                            }
                                        />
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </Tbody>
                </Table>
            </InvoicePartGridTableContainer>
        </>
    );
}
